/**
 * Builds ActiveFilter[] from the server-parsed `initialFilters` value
 * (pageContext.data.initialFilters, produced by normalizeFiltersFromUrl in +data.ts).
 *
 * Using this source instead of re-parsing the URL ensures:
 * - ALL filter types are captured (not just the 4 the old code handled)
 * - Values are already normalised/capitalised to match Algolia facet values
 * - Both flat (?remoteness=onsite) and namespaced (?en_search[refinementList][remoteness][0]=onsite) URLs work
 */

import type { ActiveFilter, FilterType, SRPLanguage } from '../../types/srp-seo-meta'
import { getRegionTagLabel } from '../facets/region-tag-labels'

/** Maps Algolia attribute names → SEO FilterType */
const ATTRIBUTE_TO_FILTER_TYPE: Record<string, FilterType> = {
  location: 'location',
  companyId: 'company',
  normalisedTitle: 'normalisedTitle',
  field: 'field',
  remoteness: 'remoteness',
  requiredLanguages: 'requiredLanguages',
  jobType: 'jobType',
  normalisedRoleLevel: 'normalisedRoleLevel',
  companyIndustry: 'companyIndustry',
  educationLevel: 'educationLevel',
  benefits: 'benefits',
  skillsAndTechnologies: 'skillsAndTechnologies',
  peopleManagementTier: 'peopleManagementTier',
  specialisations: 'specialisations',
  regionTags: 'regionTags',
}

/**
 * Extracts the Algolia attribute name from either a plain key ("remoteness")
 * or a namespaced key ("en_search[refinementList][remoteness][0]").
 */
function extractAttribute(key: string): string {
  const match = key.match(/\[refinementList\]\[([^\]]+)\]/)
  return match ? match[1] : key
}

/**
 * True if `rawKey` (plain or namespaced) maps to a FilterType `getRobotsDirective` understands.
 * Used to decide whether a query param is safe to leave unclassified (falls through to
 * `filters.length === 0 → index`) vs. must force noindex (see `compute-srp-robots-content.ts`).
 */
export function isRecognizedSrpFilterAttribute(rawKey: string): boolean {
  return Boolean(ATTRIBUTE_TO_FILTER_TYPE[extractAttribute(rawKey)])
}

/**
 * The full set of query-param attribute names `getRobotsDirective` can classify. Exported so
 * tests can assert, exhaustively, that robots.txt never blocks a URL built purely from these
 * (indexability for these must come from the meta tag, not robots.txt) and that this list never
 * overlaps with the unclassified/junk param blocklist (`seo-unclassified-query-params.ts`).
 */
export const RECOGNIZED_SRP_FILTER_ATTRIBUTES: string[] = Object.keys(ATTRIBUTE_TO_FILTER_TYPE)

function parseFiltersObject(raw: unknown): Record<string, string[]> {
  if (!raw) return {}
  if (typeof raw === 'string') {
    if (!raw.trim()) return {}
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch {
      return {}
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, string[]>
  }
  return {}
}

/**
 * Converts the server-normalised `initialFilters` map into an `ActiveFilter[]`
 * suitable for `generateSEOMeta`. One ActiveFilter is emitted per filter type;
 * multi-value selections use the first value only (most important for titles).
 */
export function buildActiveFiltersFromInitialFilters(
  initialFilters: unknown,
  locale: SRPLanguage = 'en',
): ActiveFilter[] {
  const filtersObj = parseFiltersObject(initialFilters)
  const filters: ActiveFilter[] = []
  const seen = new Set<FilterType>()

  for (const [rawKey, values] of Object.entries(filtersObj)) {
    if (!Array.isArray(values) || values.length === 0) continue

    const attribute = extractAttribute(rawKey)
    const filterType = ATTRIBUTE_TO_FILTER_TYPE[attribute]
    if (!filterType || seen.has(filterType)) continue

    const value = String(values[0])
    if (!value) continue

    seen.add(filterType)
    // regionTags values are gazetteer place ids / region codes, not display text — resolve them to
    // the localized region name so every consumer of ActiveFilter (titles, stats, headings) gets
    // the human-readable name instead of a raw id. `rawValue` keeps the place id for logic.
    const isRegionTag = filterType === 'regionTags'
    filters.push({
      type: filterType,
      value: isRegionTag ? getRegionTagLabel(value, locale) : value,
      rawValue: isRegionTag
        ? value
        : filterType === 'requiredLanguages'
          ? value.toLowerCase()
          : value,
    })
  }

  return filters
}
